import type { KeyboardEvent, MouseEvent } from 'react';

import { FromPriceDisplay } from '../PriceDisplay';
import { TextButton } from '../TextButton';
import {
  ActionButton,
  Content,
  Header,
  Image,
  ImageAction,
  ImageBadge,
  ImageWrapper,
  ItemCardBase,
  Meta,
  OverlayContent,
  OverlayPrice,
  OverlayTitle,
  Placeholder,
  PriceRow,
  StatusBadge,
  Title,
  UnavailableOverlay,
  UnavailableOverlayDivider,
  UnavailableOverlayText,
} from './ItemCard.styles';
import { CARD_IMAGE_SIZING } from '../EntityImage';
import type { ItemCardProps } from './ItemCard.types';

export function ItemCard({
  image,
  imageAspectRatio,
  imageMobileAspectRatio,
  imageOverlay = false,
  title,
  meta,
  price,
  statusBadge,
  imageBadge,
  imageAction,
  action,
  customAction,
  unavailable,
  unavailableLabel,
  editSearchLabel,
  onEditSearch,
  ariaLabel,
  variant = 'default',
  textPosition = 'left',
  boldTitle = false,
  onSelect,
}: ItemCardProps) {
  const clickable = Boolean(onSelect);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect?.();
    }
  };

  const unavailableAnnouncement = unavailable ? unavailableLabel ?? statusBadge : undefined;

  const baseProps = {
    $unavailable: unavailable,
    $clickable: clickable,
    role: clickable ? 'button' : undefined,
    tabIndex: clickable ? 0 : undefined,
    'aria-label':
      ariaLabel && unavailableAnnouncement
        ? `${ariaLabel} — ${unavailableAnnouncement}`
        : ariaLabel,
    'data-cy': 'item-card',
    onClick: clickable ? onSelect : undefined,
    onKeyDown: clickable ? handleKeyDown : undefined,
  };

  const unavailableOverlay = unavailable && unavailableLabel && (
    <UnavailableOverlay>
      <UnavailableOverlayText>{unavailableLabel}</UnavailableOverlayText>
      {onEditSearch && editSearchLabel && (
        <>
          <UnavailableOverlayDivider aria-hidden="true" />
          <TextButton
            size="xs"
            onClick={(event: MouseEvent<HTMLButtonElement>) => {
              event.stopPropagation();
              onEditSearch();
            }}
          >
            {editSearchLabel}
          </TextButton>
        </>
      )}
    </UnavailableOverlay>
  );

  if (variant === 'overlay') {
    return (
      <ItemCardBase {...baseProps}>
        <ImageWrapper
          $aspectRatio={imageAspectRatio}
          $mobileAspectRatio={imageMobileAspectRatio}
          $imageOverlay={imageOverlay}
        >
          {image.src ? (
            <Image src={image.src} alt={image.alt} sizing={CARD_IMAGE_SIZING} />
          ) : (
            <Placeholder>{image.placeholderLabel ?? 'No image'}</Placeholder>
          )}
          {unavailableOverlay}
          <OverlayContent>
            <OverlayTitle>{title}</OverlayTitle>
            {price && (
              <OverlayPrice>
                <FromPriceDisplay
                  amount={price.amount}
                  currencyCode={price.currencyCode}
                  label={price.label}
                  discount={price.discount}
                  locale={price.locale}
                  size="small"
                />
              </OverlayPrice>
            )}
          </OverlayContent>
        </ImageWrapper>
      </ItemCardBase>
    );
  }

  return (
    <ItemCardBase {...baseProps}>
      <ImageWrapper
        $aspectRatio={imageAspectRatio}
        $mobileAspectRatio={imageMobileAspectRatio}
        $imageOverlay={imageOverlay}
      >
        {image.src ? (
          <Image src={image.src} alt={image.alt} sizing={CARD_IMAGE_SIZING} />
        ) : (
          <Placeholder>{image.placeholderLabel ?? 'No image'}</Placeholder>
        )}
        {imageBadge && <ImageBadge>{imageBadge}</ImageBadge>}
        {imageAction && (
          <ImageAction
            type="button"
            onClick={(event: MouseEvent<HTMLButtonElement>) => {
              event.stopPropagation();
              imageAction.onClick();
            }}
          >
            {imageAction.label}
          </ImageAction>
        )}
        {unavailableOverlay}
      </ImageWrapper>

      <Content $textPosition={textPosition}>
        <Header>
          <Title $bold={boldTitle}>{title}</Title>
          {customAction}
          {action && (
            <ActionButton
              type="button"
              onClick={(event: MouseEvent<HTMLButtonElement>) => {
                event.stopPropagation();
                action.onClick();
              }}
            >
              {action.label}
            </ActionButton>
          )}
        </Header>

        {meta && <Meta>{meta}</Meta>}

        {statusBadge && <StatusBadge tone="neutral">{statusBadge}</StatusBadge>}

        {price && (
          <PriceRow>
            <FromPriceDisplay
              amount={price.amount}
              currencyCode={price.currencyCode}
              label={price.label}
              discount={price.discount}
              locale={price.locale}
              size="base"
            />
          </PriceRow>
        )}
      </Content>
    </ItemCardBase>
  );
}
